import { useEffect, useRef, useState } from "react";
import { isStripe } from "../utils/paymentProvider";
import { useConnectOnboarding } from "./useConnectOnboarding";
import { isQuickContractPayeeSender } from "../components/Contracts/stripeContractHelpers";

/**
 * G4 — when a quick-contract PAYEE-SENDER (a gigster who sent direct_pay_to_contractor,
 * or a contractor who sent direct_pay_to_homeowner) lands on the contract-details screen
 * and their LIVE Stripe payout status is not enabled, prompt them once per contract to
 * set up payouts. Live `payouts_enabled` (not the cached `receivingAccountVerify`) is the
 * source of truth — payouts can be re-disabled by Stripe KYC re-review after onboarding.
 * Mirrors mobile `useQuickContractPayeeOnboarding`.
 *
 * Returns `{ showPrompt, dismiss }` for the caller to render its own onboarding modal.
 */
export const useQuickContractPayeeOnboarding = (
  contractDetail,
  userType,
  dispatch
) => {
  const { status } = useConnectOnboarding(dispatch);
  const [showPrompt, setShowPrompt] = useState(false);
  const promptedForIdRef = useRef(null);

  const contractId = contractDetail?.id;
  const isPayeeSender =
    isStripe() && isQuickContractPayeeSender(contractDetail, userType);

  useEffect(() => {
    if (!isPayeeSender || !contractId) {
      return;
    }
    if (promptedForIdRef.current === contractId) {
      return;
    }
    // Wait for the live status to resolve; only prompt on an explicit false.
    if (!status) {
      return;
    }
    if (status.payouts_enabled === false) {
      promptedForIdRef.current = contractId;
      setShowPrompt(true);
    }
  }, [isPayeeSender, contractId, status]);

  const dismiss = () => setShowPrompt(false);

  return { showPrompt, dismiss };
};

export default useQuickContractPayeeOnboarding;
